/** Sessions.tsx — session history (SPEC §3 GET /sessions). */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/provider';
import type { Session, SessionStatus } from '@/types';

export default function Sessions() {
  const api = useApi();
  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.listSessions(),
  });

  // Newest-first, with active/pending sessions floated to the top so the
  // "what's happening right now" rows lead the history — derived from the
  // existing query, no extra fetch.
  const rows = useMemo<Session[]>(() => {
    const rank: Record<SessionStatus, number> = { active: 0, pending: 1, ended: 2, failed: 2 };
    const ts = (s: Session) => new Date(s.started_at ?? s.created_at).getTime() || 0;
    return [...sessions].sort((a, b) => rank[a.status] - rank[b.status] || ts(b) - ts(a));
  }, [sessions]);

  const liveCount = useMemo(
    () => sessions.filter((s) => s.status === 'active').length,
    [sessions],
  );

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2.5 text-xl font-bold text-helm-text">
            <IconBroadcast />
            Session History
          </h1>
          <p className="mt-0.5 text-sm text-helm-muted">
            Past and in-progress remote-access sessions across your organization.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`rh-badge ${liveCount > 0 ? 'bg-helm-good/15 text-helm-good' : 'bg-helm-elevated text-helm-muted'}`}
            title="Sessions currently connected"
          >
            <span className={`h-1.5 w-1.5 rounded-full ${liveCount > 0 ? 'bg-helm-good' : 'bg-helm-muted'}`} />
            {liveCount} active
          </span>
          <span className="rh-badge bg-helm-elevated text-helm-muted" title="Total sessions on record">
            {sessions.length} total
          </span>
        </div>
      </div>

      <div className="rh-card overflow-hidden">
        <table className="rh-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Device</th>
              <th>Mode</th>
              <th>Technician</th>
              <th>Started</th>
              <th>Duration</th>
              <th className="text-right">Transferred</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id}>
                <td>
                  <StatusTag status={s.status} />
                </td>
                <td>
                  <span className="inline-flex items-center gap-2">
                    <span className="text-helm-muted">
                      <IconMonitor />
                    </span>
                    <span className="font-mono text-xs text-helm-text" title={s.device_id}>
                      {s.device_id.slice(0, 8)}
                    </span>
                  </span>
                </td>
                <td>
                  <ModeTag mode={s.mode} />
                </td>
                <td className="font-mono text-xs text-helm-muted" title={s.user_id ?? undefined}>
                  {s.user_id ? s.user_id.slice(0, 8) : 'system'}
                </td>
                <td className="whitespace-nowrap text-helm-text">{fmt(s.started_at ?? s.created_at)}</td>
                <td className="whitespace-nowrap text-helm-muted">{duration(s.started_at, s.ended_at)}</td>
                <td className="text-right text-helm-muted">{fmtBytes(s.bytes_relayed)}</td>
              </tr>
            ))}
            {isLoading && (
              <tr>
                <td colSpan={7} className="py-12 text-center text-helm-muted">
                  <span className="inline-flex items-center gap-2">
                    <Spinner />
                    Loading sessions…
                  </span>
                </td>
              </tr>
            )}
            {rows.length === 0 && !isLoading && (
              <tr>
                <td colSpan={7} className="py-14 text-center">
                  <div className="flex flex-col items-center gap-3 text-helm-muted">
                    <span className="opacity-50">
                      <IconBroadcast size={32} />
                    </span>
                    <p className="text-sm">No sessions yet. Connect to a device to start one.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusTag({ status }: { status: SessionStatus }) {
  const meta: Record<SessionStatus, { label: string; tone: string; dot: string; pulse?: boolean }> = {
    active: { label: 'Active', tone: 'bg-helm-good/15 text-helm-good', dot: 'bg-helm-good', pulse: true },
    pending: { label: 'Pending', tone: 'bg-helm-warn/15 text-helm-warn', dot: 'bg-helm-warn' },
    ended: { label: 'Ended', tone: 'bg-helm-elevated text-helm-muted', dot: 'bg-helm-muted' },
    failed: { label: 'Failed', tone: 'bg-helm-bad/15 text-helm-bad', dot: 'bg-helm-bad' },
  };
  const m = meta[status] ?? meta.ended;
  return (
    <span className={`rh-badge ${m.tone}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot} ${m.pulse ? 'animate-pulse' : ''}`} />
      {m.label}
    </span>
  );
}

function ModeTag({ mode }: { mode: Session['mode'] }) {
  const control = mode === 'control';
  return (
    <span
      className={`rh-badge ${control ? 'bg-helm-accent/15 text-helm-accent' : 'bg-helm-elevated text-helm-muted'}`}
      title={control ? 'Full remote control' : 'View-only session'}
    >
      {control ? <IconControl /> : <IconEye />}
      {control ? 'Control' : 'View'}
    </span>
  );
}

/* --- inline stroke icons (currentColor) --- */
function IconBroadcast({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="2" />
      <path d="M4.9 4.9a10 10 0 0 0 0 14.2M19.1 4.9a10 10 0 0 1 0 14.2M7.8 7.8a6 6 0 0 0 0 8.4M16.2 7.8a6 6 0 0 1 0 8.4" />
    </svg>
  );
}
function IconMonitor() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}
function IconControl() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m3 3 7.5 18 2.1-7.4L20 11.4z" />
    </svg>
  );
}
function IconEye() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="2.6" />
    </svg>
  );
}
function Spinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="animate-spin text-helm-accent" aria-hidden="true">
      <path d="M12 3a9 9 0 1 0 9 9" />
    </svg>
  );
}

function fmt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function duration(start: string | null, end: string | null): string {
  if (!start) return '—';
  const a = new Date(start).getTime();
  const b = end ? new Date(end).getTime() : Date.now();
  const s = Math.max(0, Math.round((b - a) / 1000));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
